<script>
  import { onMount } from "svelte";
  import LandingPage from "./lib/components/LandingPage.svelte";
  import UploadWizard from "./lib/components/UploadWizard.svelte";
  import Icon from "./lib/components/ui/Icon.svelte";
  import { Modal, Button, ToastContainer } from "./lib/components/ui/index.js";
  import { getPublicConfig } from "./lib/api/index.js";

  let currentPage = $state("landing");
  let isLicensed = $state(true); // Default to licensed until we check

  // Splash/transition state
  let showTransitionSplash = $state(false);
  let transitionFading = $state(false);

  // Upload wizard state
  let uploadWizardRef = $state(null);
  let uploadStep = $state(1);
  let showCancelConfirm = $state(false);
  let pendingNavigation = $state(null);

  // Handle cancel upload with confirmation
  function handleCancelUpload() {
    showCancelConfirm = true;
  }

  function confirmCancel() {
    showCancelConfirm = false;
    uploadWizardRef?.reset();
    // If there's a pending navigation from browser back, execute it
    if (pendingNavigation) {
      parseUrl();
      pendingNavigation = null;
    }
  }

  function dismissCancel() {
    showCancelConfirm = false;
    // If user dismisses, push the upload URL back to stay on the page
    if (pendingNavigation) {
      window.history.pushState({}, "", "#upload");
      pendingNavigation = null;
    }
  }

  // Parse URL path to determine current page
  function parseUrl() {
    const path = window.location.pathname;
    const hash = window.location.hash.slice(1); // Remove #

    if (path === "/upload" || hash === "upload") {
      currentPage = "upload";
    } else {
      currentPage = "landing";
    }
  }

  // Update URL when navigating
  function navigate(page) {
    // Show transition splash when going to upload
    if (page === "upload" && currentPage === "landing") {
      showTransitionSplash = true;
      transitionFading = false;

      // After brief delay, start fading and switch page
      setTimeout(() => {
        currentPage = page;
        transitionFading = true;

        // Remove splash after fade completes
        setTimeout(() => {
          showTransitionSplash = false;
        }, 600);
      }, 800);
    } else {
      currentPage = page;
    }

    let newPath = "/";
    if (page === "upload") {
      newPath = "/upload";
    }

    // Use hash-based routing for compatibility without server config
    window.history.pushState({}, "", `#${newPath.slice(1)}`);
  }

  // Handle keyboard shortcuts
  function handleKeydown(event) {
    // Ctrl+Shift+A or Cmd+Shift+A to go to backend admin panel
    if (
      (event.ctrlKey || event.metaKey) &&
      event.shiftKey &&
      event.key === "A"
    ) {
      event.preventDefault();
      window.location.href = "/admin";
    }
  }

  // Handle browser back/forward
  function handlePopState() {
    // If user is in the middle of upload (steps 2-3), warn them
    if (currentPage === "upload" && uploadStep > 1 && uploadStep < 4) {
      pendingNavigation = true;
      showCancelConfirm = true;
    } else {
      parseUrl();
    }
  }

  onMount(async () => {
    parseUrl();
    window.addEventListener("keydown", handleKeydown);
    window.addEventListener("popstate", handlePopState);

    // Check license status
    try {
      const config = await getPublicConfig();
      isLicensed = config.licensing?.isLicensed !== false;
    } catch (error) {
      console.error("Failed to fetch config:", error);
      // Default to licensed if we can't check
      isLicensed = true;
    }

    return () => {
      window.removeEventListener("keydown", handleKeydown);
      window.removeEventListener("popstate", handlePopState);
    };
  });
</script>

<div class="app-layout">
  <!-- Transition Splash Screen -->
  {#if showTransitionSplash}
    <div class="transition-splash" class:transition-fading={transitionFading}>
      <div class="transition-content">
        <img
          src="/logo-splash.png"
          alt="Provider Drop"
          class="transition-logo"
        />
        <h1 class="transition-title">
          <span class="transition-title-light">PROVIDER</span><span
            class="transition-title-bold">DROP</span
          >
        </h1>
      </div>
    </div>
  {/if}

  <!-- Toast Notifications -->
  <ToastContainer />

  <!-- Unlicensed Watermark -->
  {#if !isLicensed}
    <div class="unlicensed-watermark">UNLICENSED</div>
  {/if}

  <!-- Main Content Area -->
  <main class="app-main">
    {#if currentPage === "landing"}
      <LandingPage onGetStarted={() => navigate("upload")} />
    {:else if currentPage === "upload"}
      <UploadWizard
        bind:ref={uploadWizardRef}
        onStepChange={(step) => (uploadStep = step)}
        {isLicensed}
      />
    {/if}
  </main>

  <!-- FAB Container - Only shown on upload wizard -->
  {#if currentPage === "upload"}
    <div class="fab-container">
      <!-- Navigation FABs -->
      <div class="fab-group fab-group-horizontal">
        {#if uploadStep === 4}
          <!-- On confirmation page - show New to start over -->
          <button
            class="fab fab-primary"
            onclick={() => uploadWizardRef?.reset()}
            aria-label="Start new upload"
            title="Start New Upload"
          >
            <Icon name="Plus" size={24} />
            <span class="fab-label">New</span>
          </button>
        {:else}
          <!-- Wizard navigation -->
          {#if uploadStep > 1}
            <button
              class="fab fab-secondary"
              onclick={() => uploadWizardRef?.prevStep()}
              disabled={uploadWizardRef?.isLoading}
              aria-label="Go back"
              title="Back"
            >
              <Icon name="ChevronLeft" size={24} />
              <span class="fab-label">Back</span>
            </button>
          {/if}
          {#if uploadStep === 2}
            <button
              class="fab fab-primary"
              onclick={() => uploadWizardRef?.nextStep()}
              disabled={uploadWizardRef?.isLoading}
              aria-label="Continue"
              title="Next"
            >
              <span class="fab-label">Next</span>
              <Icon name="ChevronRight" size={24} />
            </button>
          {/if}
          {#if uploadStep > 1 && uploadStep < 4}
            <button
              class="fab fab-danger fab-small"
              onclick={handleCancelUpload}
              aria-label="Cancel upload"
              title="Cancel Upload"
            >
              <Icon name="X" size={20} />
            </button>
          {/if}
        {/if}
      </div>
    </div>
  {/if}

  <!-- Cancel Confirmation Modal -->
  <Modal bind:open={showCancelConfirm} title="Cancel Upload?">
    <p class="cancel-message">
      Are you sure you want to cancel this upload? All entered information will
      be lost.
    </p>

    {#snippet actions()}
      <Button variant="secondary" onclick={dismissCancel}
        >Continue Upload</Button
      >
      <Button variant="danger" onclick={confirmCancel}>Yes, Cancel</Button>
    {/snippet}
  </Modal>
</div>

<style>
  /* Transition Splash Screen */
  .transition-splash {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 50%, #3d5a7f 100%);
    transition:
      opacity 0.6s ease-out,
      transform 0.6s ease-out;
  }

  .transition-splash.transition-fading {
    opacity: 0;
    transform: scale(1.05);
  }

  .transition-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-6);
    animation: transition-enter 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  }

  @keyframes transition-enter {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  .transition-logo {
    width: 120px;
    height: 120px;
    object-fit: contain;
    filter: drop-shadow(0 8px 32px rgba(0, 0, 0, 0.3));
  }

  .transition-title {
    font-size: 1.5rem;
    letter-spacing: 0.3em;
    color: white;
    margin: 0;
    text-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
  }

  .transition-title-light {
    font-weight: 300;
  }

  .transition-title-bold {
    font-weight: 700;
  }

  .app-layout {
    min-height: 100vh;
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow-x: clip;
    max-width: 100vw;
  }

  /* Unlicensed Watermark */
  .unlicensed-watermark {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
    font-size: 6rem;
    font-weight: 900;
    color: rgba(239, 68, 68, 0.08);
    pointer-events: none;
    z-index: 1;
    user-select: none;
    white-space: nowrap;
    letter-spacing: 0.5rem;
    text-shadow: 0 0 20px rgba(239, 68, 68, 0.1);
  }

  @media (max-width: 768px) {
    .unlicensed-watermark {
      font-size: 3rem;
      letter-spacing: 0.3rem;
    }
  }

  /* Main content */
  .app-main {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  /* FAB Container - Fixed bottom right with safe area */
  .fab-container {
    position: fixed;
    bottom: var(--space-4);
    bottom: calc(var(--space-4) + env(safe-area-inset-bottom, 0px));
    right: var(--space-4);
    right: calc(var(--space-4) + env(safe-area-inset-right, 0px));
    z-index: var(--z-fixed);
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: var(--space-3);
  }

  .fab-group {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: var(--space-3);
  }

  .fab-group-horizontal {
    flex-direction: row;
    align-items: center;
  }

  /* FAB Base Styles */
  .fab {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    border: none;
    border-radius: var(--radius-full);
    cursor: pointer;
    font-weight: var(--font-semibold);
    font-size: var(--text-sm);
    transition: all var(--transition-base);
    box-shadow: var(--shadow-lg);
    min-width: 56px;
    min-height: 56px;
    padding: var(--space-4);
  }

  .fab:active {
    transform: scale(0.95);
  }

  .fab:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  .fab:disabled:hover {
    transform: none;
  }

  /* Primary FAB - Gradient */
  .fab-primary {
    background: var(--gradient-primary);
    color: white;
    box-shadow:
      0 4px 14px rgba(139, 92, 246, 0.4),
      0 0 0 0 rgba(139, 92, 246, 0);
  }

  .fab-primary:hover {
    box-shadow:
      0 6px 20px rgba(139, 92, 246, 0.5),
      0 0 0 4px rgba(139, 92, 246, 0.1);
    transform: translateY(-2px);
  }

  /* Danger FAB - For cancel action */
  .fab-danger {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    box-shadow:
      0 4px 14px rgba(239, 68, 68, 0.4),
      0 0 0 0 rgba(239, 68, 68, 0);
  }

  .fab-danger:hover {
    box-shadow:
      0 6px 20px rgba(239, 68, 68, 0.5),
      0 0 0 4px rgba(239, 68, 68, 0.1);
    transform: translateY(-2px);
  }

  /* Small FAB variant */
  .fab-small {
    min-width: 44px;
    min-height: 44px;
    padding: var(--space-2);
  }

  /* Secondary FAB - Glass */
  .fab-secondary {
    background: var(--glass-bg);
    backdrop-filter: blur(var(--blur-md));
    -webkit-backdrop-filter: blur(var(--blur-md));
    color: var(--text-primary);
    border: 1px solid var(--glass-border);
    min-width: 48px;
    min-height: 48px;
    padding: var(--space-3);
  }

  .fab-secondary:hover {
    background: var(--glass-bg-hover);
    border-color: var(--glass-border-strong);
    box-shadow:
      var(--shadow-lg),
      0 0 0 4px rgba(255, 255, 255, 0.05);
    transform: translateY(-2px);
  }

  /* FAB Label - Hidden on mobile, shown on larger screens */
  .fab-label {
    display: none;
  }

  /* Tablet and up - Show labels on primary FAB */
  @media (min-width: 640px) {
    .fab-container {
      bottom: var(--space-6);
      right: var(--space-6);
    }

    .fab-label {
      display: inline;
    }

    .fab-primary {
      padding: var(--space-4) var(--space-6);
      border-radius: var(--radius-full);
    }
  }

  /* Desktop */
  @media (min-width: 1024px) {
    .fab-container {
      bottom: var(--space-8);
      right: var(--space-8);
    }
  }

  /* Cancel confirmation modal */
  .cancel-message {
    font-size: var(--text-base);
    color: var(--text-secondary);
    margin: 0;
    line-height: 1.6;
  }
</style>
